import { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../utils/helpers';

export interface Column<T> {
  key: string;
  header: string | ReactNode;
  render?: (item: T) => ReactNode;
  className?: string;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  title?: string;
  headerActions?: ReactNode;
  emptyMessage?: string;
  emptyDescription?: string;
  isLoading?: boolean;
  className?: string;
}

export function DataTable<T extends object>({
  data,
  columns,
  title,
  headerActions,
  emptyMessage = 'No data found',
  emptyDescription = 'No items to display',
  isLoading = false,
  className,
}: DataTableProps<T>) {
  return (
    <Card className={cn('overflow-hidden', className)}>
      {(title || headerActions) && (
        <CardHeader className="border-b bg-muted/50 px-6 py-4">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            {title && <CardTitle className="text-xl">{title}</CardTitle>}
            {headerActions && <div className="flex items-center space-x-4">{headerActions}</div>}
          </div>
        </CardHeader>
      )}
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b">
                {columns.map((column, index) => (
                  <TableHead
                    key={String(column.key) + index}
                    className={cn('h-12 px-6 font-semibold', column.className)}
                  >
                    {column.header}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-12">
                    <div className="text-muted-foreground">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : data.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} className="text-center py-12">
                    <div className="text-muted-foreground">
                      <p className="text-lg font-medium">{emptyMessage}</p>
                      <p className="text-sm mt-1">{emptyDescription}</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                data.map((item, rowIndex) => (
                  <TableRow key={rowIndex} className="hover:bg-muted/50">
                    {columns.map((column, colIndex) => (
                      <TableCell
                        key={String(column.key) + colIndex}
                        className={cn('px-6 py-4', column.className)}
                      >
                        {column.render
                          ? column.render(item)
                          : String((item as Record<string, unknown>)[column.key] ?? '-')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
